import { ChangeEvent, useEffect, useMemo, useState } from 'react';

import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { DropdownChangeParams } from 'primereact/dropdown';

import './EditProductDetail.scss';
import { IEditProductDetailProps } from './IEditProductDetailProps';
import { DropdownOption } from '../../../models/common/DropdownOption';
import { HttpService, IHttpService } from '../../../services/httpService';
import {
	INotifierService,
	NotifierService
} from '../../../services/notifierService';
import { MultiSelect } from 'primereact/multiselect';
import { ProductDetailsUpdateRequestModel } from '../../../models/productDetails/ProductDetailsUpdateRequestModel';
import { InputText } from 'primereact/inputtext';

const EditProductDetail = ({
	productName,
	productId,
	currentDivisions,
	setShouldReload
}: IEditProductDetailProps): JSX.Element => {
	const [divisionsIds, setDivisionsIds] = useState<number[]>(
		currentDivisions.map((division) => division.id)
	);

	const [divisionsOptions, setDivisionOptions] = useState<DropdownOption[]>(
		[]
	);

	const [isVisible, setIsVisible] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const httpService: IHttpService = useMemo(() => new HttpService(), []);
	const notifierService: INotifierService = useMemo(
		() => new NotifierService(),
		[]
	);

	useEffect(() => {
		httpService
			.get<DropdownOption[]>(`divisions/dropdown`, undefined)
			.then((options) => setDivisionOptions(options))
			.catch((err) => notifierService.error());
	}, [httpService, notifierService]);

	useEffect(() => {
		setDivisionsIds(currentDivisions.map((division) => division.id));
	}, [currentDivisions]);

	const resetDialog = (): void => {
		setDivisionsIds(currentDivisions.map((division) => division.id));
		setIsVisible(false);
		setIsLoading(false);
		document.body.classList.remove('no-scroll');
	};

	const handleUpdate = async (): Promise<void> => {
		setIsLoading(true);

		const productDetail: ProductDetailsUpdateRequestModel = {
			divisionsIds
		};

		try {
			await httpService.put(
				`productDetails/${productId}`,
				JSON.stringify(productDetail),
				undefined,
				false
			);
		} catch (err) {
			notifierService.error(err);
		} finally {
			setShouldReload(true);
			resetDialog();

			setIsLoading(false);
			setIsVisible(false);
			document.body.classList.remove('no-scroll');
		}
	};

	const updateDialogFooter: JSX.Element = (
		<>
			<button
				className='btn primary'
				onClick={handleUpdate}
				disabled={!divisionsIds}
			>
				Update
			</button>
			<button className='btn secondary' onClick={resetDialog}>
				Cancel
			</button>
		</>
	);

	const pendingFooter: JSX.Element = (
		<div className='edit-product-detail__dialog-footer'>
			<p>Updating Product Detail...</p>
			<ProgressSpinner />
		</div>
	);

	return (
		<>
			<i
				className='pi pi-pencil edit-product-detail__action-btn'
				onClick={() => {
					setIsVisible(true);
					document.body.classList.add('no-scroll');
				}}
			></i>

			<Dialog
				className='edit-product-detail__dialog'
				header='Update Product Detail'
				visible={isVisible}
				onHide={resetDialog}
				footer={isLoading ? pendingFooter : updateDialogFooter}
				baseZIndex={9999}
			>
				<div className='edit-product-detail__dialog-content'>
					<div className='edit-product-detail__dialog-row'>
						<p className='edit-product-detail__dialog-product-name'>
							Description
						</p>
						<p className='edit-product-detail__dialog-product-value'>
							<strong>{productName}</strong>
						</p>
					</div>
					<label htmlFor='divisionId'>Divisions</label>
					<MultiSelect
						id='divisionId'
						required
						placeholder='Select Divisions'
						value={divisionsIds}
						options={divisionsOptions}
						onChange={(e: DropdownChangeParams) => {
							setDivisionsIds(e.target.value);
						}}
					/>
				</div>
			</Dialog>
		</>
	);
};

export default EditProductDetail;
